import React, { useMemo, useRef, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../../components/ui/Button';
import { Dropdown } from 'primereact/dropdown';
import { ColorPicker } from 'primereact/colorpicker';
import { ProgressSpinner } from 'primereact/progressspinner';

import { useSelector, useDispatch } from 'react-redux';
import { uploadToGcs } from '../../../../../../lib/gcsUpload';
import { useUpdateCampaignCoverMutation } from '../../../../../../queries';

const CoverSettingsForm = ({ campaignId }) => {
    const { t } = useTranslation();
    const modeOptions = [
        { label: t('show.show_cover_image'), value: "IMAGE" },
        { label: t('show.show_cover_color'), value: "COLOR" },
    ];


    const [mode, setMode] = useState(null);
    const [color, setColor] = useState('#FFB300');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const { campaignDetails, companyDetails, isLoading } = useSelector((s) => s.global) ?? {};
    const [coverSetting, setCoverSetting] = useState(campaignDetails?.cover ?? { type: "COLOR", color: "#E53935" });


    const coverMutation = useUpdateCampaignCoverMutation();

    const getExt = (file) => {
        if (!file) return "";
        if (file.type && file.type.includes("/")) return file.type.split("/")[1];
        const dot = file.name.lastIndexOf(".");
        return dot !== -1 ? file.name.slice(dot + 1) : "";
    };

    const randomKey = (len = 30) =>
        Array.from(crypto.getRandomValues(new Uint8Array(len)))
            .map((n) => "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[n % 62])
            .join("");


    const handleSaveCoverImage = useCallback(async () => {
        if (!file || uploading) return;

        try {
            setUploading(true);
            setProgress(0);

            // 1) Build an object key for the file
            const ext = (getExt(file) || "bin")
                .replace("json+", "")
                .replace(/^avi\./, "avi");

            // Put covers under a clear prefix
            const folderPart = (localStorage.getItem("current_folder") || "root").toString().trim();
            const keyName = `${randomKey(30)}.${ext}`;
            // Authoritative company id (matches the backend's upload scope check
            // in S3MultipartController::assertScope). The old module-load
            // localStorage read was stale/empty for freshly-registered users →
            // malformed key → backend 403 → forced logout.
            const companyId = companyDetails?.company?.id || localStorage.getItem("company_id");
            if (!companyId) {
                setUploading(false);
                console.error("Cover upload aborted: company id not loaded yet");
                return;
            }
            const objectKey = `${companyId}/${folderPart}/covers/${keyName}`;

            // 2-3) Upload straight to our GCS bucket via the backend's
            //      /api/s3/* multipart flow (no AWS). Returns the public URL.
            const publicUrl = await uploadToGcs(file, objectKey, (pct) => {
                setProgress(pct);
            });

            // 4) Persist the cover via the campaign cover mutation
            await coverMutation.mutateAsync({
                id: campaignId,
                data: {
                    type: "IMAGE",
                    image_path: publicUrl,
                }
            }, {
                onSuccess: () => {
                    setCoverSetting(
                        { type: "IMAGE", image_path: publicUrl }
                    )
                }
            });

            setFile(null);
            setProgress(0);
        } catch (err) {
            console.error("Cover upload failed:", err);
        } finally {
            setUploading(false);
        }
    }, [file, uploading, coverMutation, companyDetails]);


    const handleSaveCoverColor = useCallback(async () => {
        if (!campaignId) return;
        await coverMutation.mutateAsync({
            id: campaignId,
            data: { type: "COLOR", color: color },
        }, {
            onSuccess: () => {
                setCoverSetting(
                    { type: "COLOR", color: color }
                )
            }
        });
    }, [campaignId, color, coverMutation]);




    return (
        <div className='flex gap-3 flex-col'>

            {/* Cover preview */}
            <div className="flex flex-col gap-2">
                <div className="text-sm">{t('show.show_change_cover')}</div>
                <div className="relative bg-gray-200 w-[340px] h-[220px] rounded-lg flex justify-center items-center">
                    {coverMutation.isLoading && (
                        <ProgressSpinner
                            className='absolute top-1 z-20 right-1'
                            style={{ width: '20px', height: '20px' }}
                            strokeWidth="3"
                            animationDuration="2s"
                        />
                    )}

                    <div className="relative w-[150px] h-[200px]">

                        {coverSetting?.type === "COLOR" ?
                            <div
                                className=" absolute z-10 w-[150px] h-[200px] rounded-lg border"
                                style={{ backgroundColor: coverSetting.color }}
                            />
                            :
                            <div className="relative w-[150px] h-[200px]">
                                <img
                                    src={coverSetting.image_path}
                                    alt="landing"
                                    className="absolute z-10 w-full h-full object-cover rounded-lg border"
                                />
                            </div>
                        }
                        <div className="absolute z-20  bottom-5 left-2 bg-gray-600 w-[100px] h-[17px] opacity-45 rounded-lg"></div>
                    </div>

                </div>
            </div>

            <div className="w-100 self-center"  >
                {/*  (action area) */}
                <div className="  rounded-md  ">
                    <div className="flex flex-col items-start  gap-2">
                        <label className='text-sm'>{t('show.show_cover_type')}</label>
                        <Dropdown
                            value={mode}
                            options={modeOptions}
                            onChange={(e) => setMode(e.value)}
                            className="w-full"
                            placeholder={t('show.show_select_cover_type')}
                        />


                        <div className="w-100 min-h-[120px]  mb-5 ">

                            {mode &&
                                (
                                    mode === "COLOR" ? (
                                        <div className="flex mt-4 justify-between items-center w-100 gap-4">
                                            <div className="flex gap-2">
                                                <ColorPicker
                                                    format="hex"
                                                    value={color}
                                                    onChange={(e) => setColor(`#${e.value}`)} />
                                                <div className="font-medium">
                                                    {color.toUpperCase()}
                                                </div>
                                            </div>
                                            <Button
                                                label={t('show.show_save_and_apply')}
                                                className='py-1'

                                                onClick={handleSaveCoverColor}
                                                disabled={coverMutation.isLoading}
                                            />
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <div
                                                className="border-2 border-dashed rounded-md p-6   text-sm"
                                                onDragOver={(e) => e.preventDefault()}
                                                onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]); }}
                                            >
                                                <input
                                                    type="file"
                                                    className=''
                                                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                                                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                                                />
                                                {file && <div className="mt-2">{t('show.show_selected_file', { name: file.name.length > 30 ? file.name.slice(0, 30) : file.name })}</div>}

                                            </div>

                                            <div className="flex justify-end gap-2 items-center">
                                                <Button
                                                    className='py-1'
                                                    label={uploading ? t('show.show_uploading') : t('show.show_upload_and_apply')}
                                                    onClick={handleSaveCoverImage}
                                                    disabled={!file || uploading || coverMutation.isLoading}
                                                />
                                            </div>
                                        </div>
                                    ))
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CoverSettingsForm;
